/**
 * KRA Photo Journal – Validation Utilities
 *
 * Re-exports normalisation helpers from @kra/integrations-airtable
 * and keeps photo-journal-specific Drive URL helpers.
 */

// Re-export shared helpers
export { resolveLookup, normaliseDate, coerceBool } from '@kra/integrations-airtable';

// ── Drive URL helpers (photo-journal specific) ──────────────────────────────

/**
 * Unwrap a redirect URL to find the underlying destination URL.
 */
function unwrapRedirect(raw: string): string {
  try {
    const parsed = new URL(raw);
    if (
      parsed.hostname.endsWith('facebook.com') ||
      parsed.hostname.endsWith('instagram.com')
    ) {
      const inner = parsed.searchParams.get('u');
      if (inner) return decodeURIComponent(inner);
    }
    for (const param of ['u', 'url', 'redirect', 'link']) {
      const inner = parsed.searchParams.get(param);
      if (inner && inner.includes('drive.google.com')) {
        return decodeURIComponent(inner);
      }
    }
  } catch {
    // URL parse failed — use the raw string as-is
  }
  return raw;
}

/**
 * Extract a Google Drive folder ID from any supported folder URL.
 */
export function extractDriveFolderId(url: string): string | null {
  if (!url || typeof url !== 'string') return null;
  const resolved = unwrapRedirect(url.trim());
  const folderMatch = resolved.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (folderMatch?.[1]) return folderMatch[1];
  const openMatch = resolved.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch?.[1]) return openMatch[1];
  return null;
}

/**
 * Return true if the given string looks like a valid Airtable record ID.
 */
export function isValidRecordId(id: string): boolean {
  return typeof id === 'string' && /^rec[a-zA-Z0-9]{14}$/.test(id);
}
